import { websocket, PeerIdType, PeerMessageType, MetricMessageType, MetricSourceType } from '@playcastdotio/Messaging';
import constants from '@playcastdotio/GlobalConfig';
import type { PlaycastWebrtcQualityRawStats, PeerQualityRecommendation } from '@playcastdotio/CoreTypes';
import { setHostBroadcastId } from '../../api/modules/realtime/hostCorrelation';
import { sendMessage } from './RealtimeConnection';

// Storage for peer IDs and tokens to support reconnection
interface StoredPeerData {
  peerId: string;
  peerToken: string;
  peerIdType: PeerIdType;
}

const storedPeerData: Record<PeerIdType, StoredPeerData | null> = {
  [PeerIdType.HOST]: null,
  [PeerIdType.PLAYER]: null,
  [PeerIdType.REMOTE_CLIENT]: null,
  [PeerIdType.REMOTE_HOST]: null,
  [PeerIdType.ADMIN]: null,
};

// Helper function to access localStorage safely
const getLocalStorage = (key: string): StoredPeerData | null => {
  return null;
};

const setLocalStorage = (key: string, value: StoredPeerData): boolean => {
  return true;
};

/** Existing peer/token persistence recovery, kept outside socket ownership. */
export function restorePeerData(): void {
  Object.values(PeerIdType).forEach((type) => {
    const storedData = getLocalStorage(`playcast_peer_${type}`);
    if (storedData) {
      storedPeerData[type] = storedData;
      console.log(`[Realtime] Loaded stored peer data for ${type}`, { peerId: storedData.peerId });
    }
  });
}

// Modified to store peer IDs and tokens with localStorage persistence
export const requestPeerId = (peerIdType: PeerIdType) => {
  // Check memory first
  let storedData = storedPeerData[peerIdType];

  // If not in memory, try localStorage
  if (!storedData) {
    const storedJson = getLocalStorage(`playcast_peer_${peerIdType}`);
    if (storedJson) {
      storedData = storedJson;
      // Also update the in-memory cache
      storedPeerData[peerIdType] = storedData;
      console.log(`[Realtime] Loaded peer data from localStorage for ${peerIdType}`, { peerId: storedData?.peerId });
    }
  }

  if (storedData) {
    console.log(`[Realtime] Attempting to reclaim peer ID for ${peerIdType}`, storedData.peerId);
    sendMessage(
      websocket.generators.peer(PeerMessageType.PEER_GET_ID, {
        peerIdType,
        peerId: storedData.peerId,
        peerToken: storedData.peerToken,
      }),
    );
  } else {
    // Request a new peer ID if none is stored
    console.log(`[Realtime] Requesting new peer ID for ${peerIdType}`);
    sendMessage(websocket.generators.peer(PeerMessageType.PEER_GET_ID, { peerIdType }));
  }
};

// Export a function to store peer IDs and tokens when received
export const storePeerData = (peerIdType: PeerIdType, peerId: string, peerToken?: string) => {
  if (peerToken) {
    // Store in memory
    const peerData = { peerId, peerToken, peerIdType };
    storedPeerData[peerIdType] = peerData;

    // The host's HOST peer id IS its broadcast id; expose it so outbound
    // messages stamp _broadcastId once broadcasting begins.
    if (peerIdType === PeerIdType.HOST) {
      setHostBroadcastId(peerId);
    }

    // Also store in localStorage for persistence across refreshes
    setLocalStorage(`playcast_peer_${peerIdType}`, peerData);

    console.log(`[Realtime] Stored peer data for ${peerIdType}`, { peerId, tokenPreview: peerToken.substring(0, 5) + '...' });
  }
};

// Export a function to clear peer data for a specific peer ID type
export const clearPeerData = (peerIdType: PeerIdType) => {
  // Clear from memory
  storedPeerData[peerIdType] = null;

  if (peerIdType === PeerIdType.HOST) {
    setHostBroadcastId(undefined);
  }

  // Clear from localStorage if we're using it
  try {
    localStorage.removeItem(`playcast_peer_${peerIdType}`);
  } catch (e) {
    // Ignore localStorage errors
  }

  console.log(`[Realtime] Cleared peer data for ${peerIdType}`);
};

// Host-side WebRTC stats type
export type WebrtcQualityStatsMetric = {
  recommendation: PeerQualityRecommendation;
  reason: string;
  rawStats: PlaycastWebrtcQualityRawStats;
};

// Host-side function to send WebRTC stats for a specific connected peer
export const sendHostMetricBatchWebrtcStats = (
  stats: WebrtcQualityStatsMetric[],
  collectionInterval: number,
  peerMetadata: {
    playerConnectionId: string;
    persistentId: string;
    playerAuthId: string;
    broadcastSessionId: string;
    inviteId: string;
  }
) => {
  console.log('[Host] Sending webrtc quality stats for peer:', peerMetadata.playerConnectionId);
  const message = websocket.generators.metrics(MetricMessageType.METRICS_WEBRTC_QUALITY, {
    payload: {
      name: 'webrtc_quality',
      timestamp: new Date().toISOString(),
      payload: {
        sourceType: MetricSourceType.HOST, // HOST perspective
        collectionInterval,
        broadcastSessionId: peerMetadata.broadcastSessionId,
        environment: constants.ENVIRONMENT,
        inviteId: peerMetadata.inviteId,
        persistentId: peerMetadata.persistentId,
        playerAuthId: peerMetadata.playerAuthId,
        playerConnectionId: peerMetadata.playerConnectionId,
        version: constants.VERSION,
        qualityStats: stats,
      },
    },
  });
  sendMessage(message);
};
